import {
  AutomationType,
  useUpdateAnalysisAutomationTasksMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { AutomationTaskActions } from 'modules/analyse/utils/formatAutomationTasksForSubmission';
import { ToastType, useToast } from 'services/toast';
import { useDrawer } from 'services/drawer/hooks';
import { FailedMessageWithHref } from 'common/ui';

type UseAnalyseUpdateAutomationTasksProps = {
  candidateBatchId: string;
  automationType: AutomationType;
};

type SubmitFormProps = {
  automationTasksActions: AutomationTaskActions;
  overwriteLockedCandidates: boolean;
  saveOnly: boolean;
};

type UseAnalyseUpdateAutomationTasksReturn = {
  submitForm: (props: SubmitFormProps) => Promise<void>;
};

export const useAnalyseUpdateAutomationTasks = ({
  automationType,
  candidateBatchId
}: UseAnalyseUpdateAutomationTasksProps): UseAnalyseUpdateAutomationTasksReturn => {
  const { showToast } = useToast();
  const { forceCloseDrawer } = useDrawer();

  const [updateAnalysisAutomationTasksMutation] =
    useUpdateAnalysisAutomationTasksMutation();

  const submitForm = async ({
    automationTasksActions,
    overwriteLockedCandidates,
    saveOnly
  }: SubmitFormProps) => {
    try {
      await updateAnalysisAutomationTasksMutation({
        variables: {
          input: {
            candidateBatchId,
            analysisAutomationTasksToCreate:
              automationTasksActions.automationTasksToCreate,
            analysisAutomationTasksToUpdate:
              automationTasksActions.automationTasksToUpdate,
            analysisAutomationTasksToDelete:
              automationTasksActions.automationTasksToDelete,
            overwriteLockedCandidates,
            automationType,
            saveOnly
          }
        }
      });

      const toastMessage =
        automationType === AutomationType.Rule
          ? 'Rules updated'
          : `Actions ${saveOnly ? 'updated' : 'initiated'}`;

      showToast({
        title: toastMessage,
        type: ToastType.Success
      });

      forceCloseDrawer();
    } catch (error) {
      const errorToastMessage =
        automationType === AutomationType.Rule
          ? 'updating the rules'
          : `${saveOnly ? 'updating' : 'applying'} the actions`;

      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref
            failedMessage={`Try ${errorToastMessage} again.`}
            emailSubject={`Failed ${errorToastMessage}`}
          />
        ),
        type: ToastType.Error
      });
    }
  };

  return {
    submitForm
  };
};
